import { ReactNode, useCallback, useMemo } from 'react'
import { createContainer } from 'unstated-next'

import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { formatPrettyCurrency } from '../../../lib/utils/currency'
import { PlacementId } from '../combination-rules/placementIds'
import { EMBROIDERY_TYPES, EmbroideryTypeKey } from '../combination-rules/types'
import {
  EmbroideryPriceKey,
  priceKeyForItem,
  priceKeyForPlacement,
  setupChargeCount,
  withoutSetupChargedElsewhere,
} from '../pricing/embroideryPrices'
import { EmbroideryPurpose } from '../types'
import {
  filterEmbroideryVariantByTags,
  findDigitizationVariant,
  useEmbroideryVariantsQuery,
} from '../useEmbroideryVariants'
import { useNewLogoInOrder } from '../useNewLogoInOrder'

import { useEmbroideryGarment } from './EmbroideryGarmentProvider'
import { CATALOG_TYPE_BY_TYPE_KEY } from './embroideryTypeKeys'
import { resolveV2EmbroideryVariants } from './resolveV2EmbroideryVariants'
import { EmbroideryItem } from './types'

const DEFAULT_CURRENCY = 'USD'

export type EmbroideryPricingProviderProps = {
  embroideryPurpose: EmbroideryPurpose
}

/**
 * What embroidery costs on this garment, priced from the catalog.
 *
 * Prices come from the same `embroideryVariants` query the rest of the modal buys from, matched
 * to a piece of embroidery by catalog key.
 */
function useEmbroideryPricingImpl(props?: EmbroideryPricingProviderProps) {
  const { embroideryPurpose } = props!
  const { catalogTypeKeys } = useEmbroideryGarment()
  const {
    data: variantsData,
    previousData: previousVariantsData,
    status: variantsStatus,
  } = useEmbroideryVariantsQuery()
  const { locale } = useLocalization()
  const newLogoInOrder = useNewLogoInOrder()

  /**
   * The flow's priced variants whose embroidery types this garment can actually produce.
   */
  const embroideryVariants = useMemo(() => {
    if (catalogTypeKeys.length === 0) return []

    const supportedTags = catalogTypeKeys.map(typeKey => CATALOG_TYPE_BY_TYPE_KEY[typeKey])
    const unsupportedTags = EMBROIDERY_TYPES.filter(
      typeKey => !catalogTypeKeys.includes(typeKey)
    ).map(typeKey => CATALOG_TYPE_BY_TYPE_KEY[typeKey])

    const variants = (variantsData ?? previousVariantsData)?.embroideryVariants ?? []

    return variants.filter(variant =>
      filterEmbroideryVariantByTags(variant, {
        tagsAllOf: [embroideryPurpose === 'B2B' ? 'B2B' : 'DTC'],
        tagsOneOf: supportedTags,
        tagsNoneOf: unsupportedTags,
      })
    )
  }, [catalogTypeKeys, embroideryPurpose, previousVariantsData, variantsData])

  const digitizationVariant = useMemo(
    () => findDigitizationVariant((variantsData ?? previousVariantsData)?.embroideryVariants),
    [previousVariantsData, variantsData]
  )

  // Every price below is read from the variants the bag buys, so the two can't disagree.
  const v2EmbroideryVariants = useMemo(
    () => resolveV2EmbroideryVariants(embroideryVariants, digitizationVariant),
    [digitizationVariant, embroideryVariants]
  )

  const currencyCode = embroideryVariants[0]?.productInfo.price.currencyCode ?? DEFAULT_CURRENCY

  /** The catalog price, or undefined when the catalog has no product for this key. */
  const getPriceInCents = useCallback(
    (priceKey: EmbroideryPriceKey): number | undefined => {
      const variant = v2EmbroideryVariants.byPriceKey[priceKey]
      return variant === undefined ? undefined : Math.round(variant.productInfo.price.amount * 100)
    },
    [v2EmbroideryVariants]
  )

  /** Undefined in, undefined out, so a missing price reads as no label. */
  const formatPrice = useCallback(
    (amountInCents: number | undefined, truncateTrailingZeros: boolean = false) =>
      amountInCents === undefined
        ? undefined
        : formatPrettyCurrency({
            value: amountInCents,
            unit: 'cents',
            currency: currencyCode,
            locale,
            truncateTrailingZeros,
          }),
    [currencyCode, locale]
  )

  const priceInCentsForPlacement = useCallback(
    (typeKey: EmbroideryTypeKey, placementId: PlacementId): number | undefined => {
      const priceKey = priceKeyForPlacement(typeKey, placementId)
      return priceKey === undefined ? undefined : getPriceInCents(priceKey)
    },
    [getPriceInCents]
  )

  const priceInCentsForItem = useCallback(
    (item: EmbroideryItem): number | undefined => {
      const priceKey = priceKeyForItem(item)
      return priceKey === undefined ? undefined : getPriceInCents(priceKey)
    },
    [getPriceInCents]
  )

  /**
   * What a set of items costs, setup aside. Undefined when any piece has no product to price it,
   * so a partial sum is never shown. An item with nothing to price yet, like a logo with no file,
   * adds nothing.
   */
  const piecesInCentsForItems = useCallback(
    (items: readonly EmbroideryItem[]): number | undefined => {
      let piecesInCents = 0
      for (const item of items) {
        const priceKey = priceKeyForItem(item)
        if (priceKey === undefined) continue

        const priceInCents = getPriceInCents(priceKey)
        if (priceInCents === undefined) return undefined
        piecesInCents += priceInCents
      }
      return piecesInCents
    },
    [getPriceInCents]
  )

  /**
   * How many setup charges a set of items adds. A new logo another garment in the order already
   * pays to digitize isn't charged again here.
   */
  const setupCountForItems = useCallback(
    (items: readonly EmbroideryItem[]): number =>
      setupChargeCount(withoutSetupChargedElsewhere(items, newLogoInOrder)),
    [newLogoInOrder]
  )

  /**
   * What setup costs for a set of items: 0 when none needs it, undefined when one does and the
   * catalog has no setup product.
   */
  const setupInCentsForItems = useCallback(
    (items: readonly EmbroideryItem[]): number | undefined => {
      const setupCount = setupCountForItems(items)
      if (setupCount === 0) return 0

      const setupPriceInCents = getPriceInCents('logo_setup')
      return setupPriceInCents === undefined ? undefined : setupCount * setupPriceInCents
    },
    [getPriceInCents, setupCountForItems]
  )

  /** What a set of items costs, setup included. Undefined when any part of it has no product. */
  const totalInCentsForItems = useCallback(
    (items: readonly EmbroideryItem[]): number | undefined => {
      const piecesInCents = piecesInCentsForItems(items)
      const setupInCents = setupInCentsForItems(items)
      return piecesInCents === undefined || setupInCents === undefined
        ? undefined
        : piecesInCents + setupInCents
    },
    [piecesInCentsForItems, setupInCentsForItems]
  )

  const lowestPriceInCentsForType = useCallback(
    (typeKey: EmbroideryTypeKey, placementIds: readonly PlacementId[]): number | undefined => {
      if (placementIds.length === 0) return undefined

      // A logo's price depends on a color not chosen yet, so it starts from the cheaper one.
      const prices =
        typeKey === 'logo'
          ? [getPriceInCents('logo:bw'), getPriceInCents('logo:full_color')]
          : placementIds.map(placementId => priceInCentsForPlacement(typeKey, placementId))
      const offeredPrices = prices.filter((price): price is number => price !== undefined)

      return offeredPrices.length === 0 ? undefined : Math.min(...offeredPrices)
    },
    [getPriceInCents, priceInCentsForPlacement]
  )

  return {
    embroideryVariants,
    digitizationVariant,
    v2EmbroideryVariants,
    variantsStatus,
    currencyCode,
    formatPrice,
    getPriceInCents,
    priceInCentsForItem,
    priceInCentsForPlacement,
    lowestPriceInCentsForType,
    piecesInCentsForItems,
    setupCountForItems,
    setupInCentsForItems,
    totalInCentsForItems,
  }
}

const EmbroideryPricingContainer = createContainer(useEmbroideryPricingImpl)

export const EmbroideryPricingProvider = ({
  initialState,
  children,
}: {
  initialState: EmbroideryPricingProviderProps
  children?: ReactNode
}) => (
  <EmbroideryPricingContainer.Provider initialState={initialState}>
    {children}
  </EmbroideryPricingContainer.Provider>
)

export const useEmbroideryPrices = EmbroideryPricingContainer.useContainer
